import { CommonModule } from '@angular/common';
import { Component, Inject, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { OfficerService } from '@app/modules/officer/services/officer.service';
import { ServiceSupportedCountry } from '@app/modules/service/models/service-supported-country.model';
import { ServiceSupportedCountryService } from '@app/modules/service/services/service-supported-country.service';
import { AppRoutes, APP_ROUTES } from '@config/app-routes.config';
import { OfficerType, OfficerTypeEnum } from '@generated/graphql';
import { BusinessPlanService } from '@modules/account-settings/pages/business-profile/services/business-plan.service';
import { ACTIVE_USER } from '@modules/auth/providers/auth.provider';
import { CompanyDirector } from '@modules/company/models/company-director.model';
import { CompanyShareholder } from '@modules/company/models/company-shareholder.model';
import { Company } from '@modules/company/models/company.model';
import { USE_DETAIL_OF } from '@modules/officer/models/types/officer-selection.const';
import { SupportedCountry } from '@modules/supported-country/models/supported-country.model';
import { SupportedCountryService } from '@modules/supported-country/services/supported-country.service';
import { User } from '@modules/user/models/user.model';
import { LetModule } from '@ngrx/component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OfficerInputHelpers } from '@shared/classes/officer/officer-input.helper';
import { CorporateInfoComponent } from '@shared/component/company/common/corporate-info/corporate-info.component';
import { IndividualInfoComponent } from '@shared/component/company/common/individual-info/individual-info.component';
import { ExistsOfficerType } from '@shared/component/job/component/company-incorporation/dialog/add-officer-dialog/classes/exists-officer.type';
import { LookupOfficerDialogComponent } from '@shared/component/job/component/company-incorporation/dialog/lookup-officer-dialog/lookup-officer-dialog.component';
import { CalloutComponent } from '@shared/component/message/callout/callout.component';
import { ExistsOfficersSelectComponent } from '@shared/component/officer/exists-officers-select/exists-officers-select.component';
import { OfficerInputReviewInformationComponent } from '@shared/component/officer/officer-input-review-information/officer-input-review-information.component';
import { SelectOption } from '@shared/helpers/convention.helpers';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { UseDetailOfEnum } from '@shared/types/commonEnum';
import { OfficerRoleEnum } from '@shared/types/officerEnum';
import { BehaviorSubject, combineLatest, filter, first, Observable, of, switchMap, tap } from 'rxjs';
import { startCase } from 'lodash-es';
import { DirectorOfficerRequestForm } from './classes/director-officer-request-form.class';
import { OfficerRequestFormState } from './classes/officer-request-form-state';
import { ShareholderOfficerRequestForm } from './classes/shareholder-officer-request-form.class';

type DialogData = {
  companyProfile: Company;
  officerRole: OfficerRoleEnum;
  selectedOfficer: CompanyDirector | CompanyShareholder;
};

@UntilDestroy()
@Component({
  selector: 'app-officer-resolution-request-dialog',
  standalone: true,
  templateUrl: './officer-resolution-request-dialog.component.html',
  styleUrls: ['./officer-resolution-request-dialog.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    LetModule,
    MatStepperModule,
    MatInputModule,
    MatDatepickerModule,
    MatDividerModule,
    MatRadioModule,
    FormsModule,
    MatButtonModule,
    MatSelectModule,
    CalloutComponent,
    OfficerInputReviewInformationComponent,
    IndividualInfoComponent,
    CorporateInfoComponent,
    ReactiveFormsModule,
    ExistsOfficersSelectComponent,
  ],
})
export class OfficerResolutionRequestDialogComponent implements OnInit {
  private readonly directorRequestState = inject(DirectorOfficerRequestForm);
  private readonly shareholderRequestState = inject(ShareholderOfficerRequestForm);

  private companyProfileState: Company;
  private selectedOfficerState: CompanyDirector | CompanyShareholder | null;
  private officerRoleState: OfficerRoleEnum;

  protected readonly OfficerTypeEnum = OfficerTypeEnum;
  protected readonly startCase = startCase;

  public readonly businessPlan$ = this.businessPlanService.businessPlan$;
  public readonly supportedCountry$: Observable<SupportedCountry>;
  public readonly detailModeOptions: SelectOption[];

  public selectedDetailMode = UseDetailOfEnum.New;
  public selectedOfficerType = OfficerTypeEnum.Individual;
  public officerRequestState: OfficerRequestFormState;
  public submitting$ = new BehaviorSubject(false);
  public supportedCountry: SupportedCountry;

  constructor(
    @Inject(APP_ROUTES) protected readonly appRoutes: AppRoutes,
    @Inject(ACTIVE_USER) protected readonly activeUser$: Observable<User>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly dialogRef: MatDialogRef<OfficerResolutionRequestDialogComponent>,
    private readonly businessPlanService: BusinessPlanService,
    protected readonly supportedCountryService: SupportedCountryService,
    private readonly snackBarService: SnackBarService,
    private readonly serviceSupportedCountryService: ServiceSupportedCountryService,
    private readonly router: Router,
    private readonly officerService: OfficerService,
    private readonly dialog: MatDialog,
    private readonly officerInputHelpers: OfficerInputHelpers,
  ) {
    this.initData();
    this.detailModeOptions = USE_DETAIL_OF(this.officerRole);
    this.officerRequestState = this.resolveOfficerRequestState();
    this.officerRequestState.role = this.officerRole;
    this.officerRequestState.mode = this.isEditMode() ? 'Edit' : 'New';
    this.supportedCountry$ = this.supportedCountryService.findByCountryId$(
      this.companyProfile.country.id,
    );
  }

  public ngOnInit(): void {
    this.initializeRequestState();
  }

  public get companyProfile(): Company {
    return this.companyProfileState;
  }

  public get officerRole(): OfficerRoleEnum {
    return this.officerRoleState;
  }

  public get selectedOfficer(): CompanyDirector | CompanyShareholder | null {
    return this.selectedOfficerState;
  }

  public isEditMode(): boolean {
    return !!this.selectedOfficer;
  }

  public isDirector(): boolean {
    return this.officerRole === OfficerRoleEnum.DIRECTOR;
  }

  public isShareholder(): boolean {
    return this.officerRole === OfficerRoleEnum.SHAREHOLDER;
  }

  public isNominee(): boolean {
    return this.selectedOfficerType === OfficerTypeEnum.Nominee;
  }

  public showExistingOfficer(): boolean {
    return this.selectedDetailMode === UseDetailOfEnum.Existing;
  }

  public isOfficerTypeSelectDisabled(): boolean {
    return (
      this.selectedDetailMode === UseDetailOfEnum.Existing ||
      this.isEditMode() ||
      (this.officerRequestState.formInput.officerForm.disabled &&
        this.selectedOfficerType !== OfficerTypeEnum.Nominee)
    );
  }

  public hideCorporateSelect(): boolean {
    return (
      this.isDirector() &&
      this.supportedCountry?.country.code.toUpperCase() === 'SG'
    );
  }

  public isOfficerTypeDisabled(officerType: OfficerTypeEnum): boolean {
    if (!this.isDirector()) {
      return false;
    }

    const nomineeExists = this.companyProfile.directors.find(
      (director) => director.isNominee,
    );

    return !!nomineeExists && officerType === OfficerTypeEnum.Nominee;
  }

  public onCloseDialog(): void {
    this.closeDialog();
  }

  public onOfficerTypeChange({ value }: MatSelectChange): void {
    this.selectedOfficerType = value;
    this.officerRequestState.selectedOfficerType = value;
    this.officerRequestState.createForm();
  }

  public onDetailModeChange({ value }: MatRadioChange): void {
    if (value !== UseDetailOfEnum.New.toString()) {
      return;
    }

    this.selectedOfficerType = OfficerTypeEnum.Individual;
    this.officerRequestState.selectedOfficerType = this.selectedOfficerType;
    this.useNewOfficerForm();
  }

  public onReviewRequest(): void {
    // Kept for template compatibility with the stepper action.
  }

  public onSubmitRequest(): void {
    this.officerRequestState.submitForm();

    this.officerRequestState.postStatus$
      .pipe(
        filter((status) => status === 'SUCCESS' || status === 'ERROR'),
        first(),
        untilDestroyed(this),
      )
      .subscribe((status) => {
        if (status === 'SUCCESS') {
          this.handleSubmitSuccess();
          return;
        }

        this.snackBarService.pushErrorMessage();
      });
  }

  public onExistingOfficerSelected(existingOfficer: ExistsOfficerType): void {
    this.selectedOfficerType = existingOfficer.type;
    this.officerRequestState.selectedOfficerType = existingOfficer.type;
    this.officerRequestState.applyExistingOfficerSelection(existingOfficer);
  }

  public openLookupOfficerDialog(): void {
    const lookupOfficerDialog = this.dialog.open(LookupOfficerDialogComponent, {
      width: '50%',
      data: {
        countryOfIncorporation: this.supportedCountry,
        hideCorporate: this.hideCorporateSelect(),
        exceptOfficerIds:
          this.officerRequestState.existingOfficerState.excludedOfficerIds,
      },
    });

    lookupOfficerDialog
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result: { officer: OfficerType; type: OfficerTypeEnum }) => {
        if (!result) {
          return;
        }

        const { officer: foundOfficer, type } = result;

        this.selectedOfficerType = type;
        this.officerRequestState.selectedOfficerType = type;
        this.officerRequestState.applyLookupOfficerSelection(foundOfficer);
      });
  }

  public resetForm(): void {
    this.selectedDetailMode = UseDetailOfEnum.New;

    if (this.isEditMode()) {
      this.selectedOfficerType = this.resolveInitialOfficerType();
      this.officerRequestState.selectedOfficerType = this.selectedOfficerType;
      this.officerRequestState.createForm();
      this.officerRequestState.setEditData(this.selectedOfficer);
      return;
    }

    this.selectedOfficerType = OfficerTypeEnum.Individual;
    this.officerRequestState.selectedOfficerType = this.selectedOfficerType;
    this.useNewOfficerForm();
  }

  public getFormType(): string | null {
    if (this.isShareholder()) {
      return 'CompanyShareholderType';
    }

    if (this.isDirector()) {
      return 'CompanyDirectorType';
    }

    return null;
  }

  public getOldOfficerInputValue(): unknown {
    if (this.isDirector()) {
      return this.officerInputHelpers.transformDirectorModelToInput(
        this.selectedOfficer as CompanyDirector,
      );
    }

    if (this.isShareholder()) {
      return this.officerInputHelpers.transformShareholderModelToInput(
        this.selectedOfficer as CompanyShareholder,
      );
    }

    return null;
  }

  private initData(): void {
    this.companyProfileState = this.data.companyProfile;
    this.selectedOfficerState = this.data.selectedOfficer ?? null;
    this.officerRoleState = this.data.officerRole;
  }

  private resolveOfficerRequestState(): OfficerRequestFormState {
    return this.isDirector()
      ? this.directorRequestState
      : this.shareholderRequestState;
  }

  private initializeRequestState(): void {
    combineLatest([
      this.activeUser$.pipe(first()),
      this.supportedCountry$.pipe(first()),
    ])
      .pipe(
        switchMap(([activeUser, supportedCountry]) => {
          this.applyBaseRequestState(activeUser, supportedCountry);

          return this.serviceSupportedCountryService
            .getByCountryId$(supportedCountry.country.id)
            .pipe(
              tap((services) =>
                this.applySupportedServices(supportedCountry, services),
              ),
              switchMap(() =>
                this.loadAdditionalOfficers$(activeUser, supportedCountry),
              ),
              tap((officers) =>
                this.applyExistingOfficerContext(activeUser, officers),
              ),
            );
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private applyBaseRequestState(
    activeUser: User,
    supportedCountry: SupportedCountry,
  ): void {
    this.supportedCountry = supportedCountry;
    this.selectedOfficerType = this.resolveInitialOfficerType();
    this.officerRequestState.activeUser = activeUser;
    this.officerRequestState.company = this.companyProfile;
    this.officerRequestState.selectedOfficerType = this.selectedOfficerType;
    this.officerRequestState.countryOfIncorporation = supportedCountry;
    this.initializeOfficerForm();
  }

  private initializeOfficerForm(): void {
    this.officerRequestState.createForm();

    if (this.isEditMode()) {
      this.officerRequestState.setEditData(this.selectedOfficer);
    }
  }

  private resolveInitialOfficerType(): OfficerTypeEnum {
    if (!this.isEditMode()) {
      return OfficerTypeEnum.Individual;
    }

    return this.selectedOfficer?.officer?.officerType === OfficerTypeEnum.Corporate
      ? OfficerTypeEnum.Corporate
      : OfficerTypeEnum.Individual;
  }

  private applySupportedServices(
    supportedCountry: SupportedCountry,
    services: ServiceSupportedCountry[],
  ): void {
    this.officerRequestState.jobOrderState.incorporationServices = services.filter(
      (service) => service.supportedCountry.id === supportedCountry.id,
    );
    this.officerRequestState.setServiceSupportedCountry(services);
  }

  private loadAdditionalOfficers$(
    activeUser: User,
    supportedCountry: SupportedCountry,
  ): Observable<OfficerType[]> {
    if (!this.isDirector() || !activeUser.isCustomer()) {
      return of([]);
    }

    return this.officerService.allOfficersByCustomer(
      supportedCountry.country.code,
    );
  }

  private applyExistingOfficerContext(
    activeUser: User,
    additionalOfficers: OfficerType[],
  ): void {
    if (this.isDirector()) {
      this.officerRequestState.existingOfficerState.targetRole = 'Director';
      this.officerRequestState.existingOfficerState.setExcludedOfficerIds(
        this.companyProfile,
      );
      this.officerRequestState.existingOfficerState.appendOfficersFromServer(
        this.companyProfile.shareholders.map((shareholder) => shareholder.officer),
      );

      if (activeUser.isCustomer()) {
        this.officerRequestState.existingOfficerState.appendOfficersFromServer(
          additionalOfficers,
        );
      }

      this.officerRequestState.normalizeExistingOfficerChoices();
      return;
    }

    if (this.isShareholder()) {
      this.officerRequestState.existingOfficerState.targetRole = 'Shareholder';
    }
  }

  private useNewOfficerForm(): void {
    this.officerRequestState.createForm();

    Promise.resolve().then(() => {
      this.officerRequestState.formInput.officerForm.enable();
      this.officerRequestState.formInput.officerForm.reset();
      this.officerRequestState.existingOfficerState.selectedOfficerId = undefined;
    });
  }

  private handleSubmitSuccess(): void {
    const createdJob = this.officerRequestState.createdJob;

    if (createdJob) {
      this.snackBarService.pushAlert(this.resolveSuccessMessage(true));
      this.router.navigate(this.appRoutes.jobDetail(createdJob.id));
      this.closeDialog();
      return;
    }

    this.snackBarService.pushAlert(this.resolveSuccessMessage(false));
    this.closeDialog(true);
  }

  private resolveSuccessMessage(hasCreatedJob: boolean): string {
    if (this.officerRequestState.mode === 'New') {
      return hasCreatedJob
        ? 'Add new officer request has been submitted!'
        : 'Add new officer has been submitted!';
    }

    return hasCreatedJob
      ? 'Edit officer request has been submitted!'
      : 'Update officer has been submitted!';
  }

  private closeDialog(result?: boolean): void {
    Promise.resolve().then(() => {
      this.dialogRef.close(result);
    });
  }
}
